import { RESERVED_SHOP_SLUGS } from './reservedShopSlugs'

/** Normalize browser/server host for custom-domain lookup. */
export function normalizeClientHost(input: string | null | undefined): string {
  if (!input) return ''
  let value = String(input).trim().toLowerCase()
  value = value.replace(/^https?:\/\//i, '')
  value = value.split('/')[0] || ''
  value = value.split(':')[0] || ''
  value = value.replace(/^www\./, '')
  return value
}

export function normalizeShopDomain(input: string | null | undefined): string | null {
  const value = normalizeClientHost(input)
  if (!value) return null
  if (
    !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(value)
    && !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(value)
  ) {
    return null
  }
  return value
}

export interface ResolvedShopDomain {
  id: number
  slug: string
  name: string
  customDomain: string | null
  appDomain?: string | null
  logo?: string | null
  status?: string
}

function isValidShopSlug(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{1,78}[a-z0-9])?$/.test(slug)
}

export function platformRootDomain(siteUrl?: string): string {
  if (siteUrl) return normalizeClientHost(siteUrl)
  try {
    const config = useRuntimeConfig()
    const fromConfig = normalizeClientHost(String(config.public.siteUrl || ''))
    if (fromConfig) return fromConfig
  } catch {
    // ignore
  }
  return 'novartshop.ro'
}

export function tenantSubdomainsEnabled(root = platformRootDomain()): boolean {
  if (!root || root === 'localhost' || root === '127.0.0.1') return false
  return root.includes('.')
}

export function tenantSubdomain(slug: string | null | undefined, root = platformRootDomain()): string | null {
  if (!tenantSubdomainsEnabled(root)) return null
  const value = String(slug || '').trim().toLowerCase()
  if (!value || !isValidShopSlug(value) || RESERVED_SHOP_SLUGS.has(value)) return null
  return `${value}.${root}`
}

export function slugFromPlatformSubdomain(input: string | null | undefined, root = platformRootDomain()): string | null {
  const domain = normalizeShopDomain(input)
  if (!domain || !tenantSubdomainsEnabled(root) || domain === root) return null
  const suffix = `.${root}`
  if (!domain.endsWith(suffix)) return null
  const label = domain.slice(0, -suffix.length)
  if (!label || label.includes('.')) return null
  if (!isValidShopSlug(label) || RESERVED_SHOP_SLUGS.has(label)) return null
  return label
}

export function isPlatformHost(input: string | null | undefined, root = platformRootDomain()): boolean {
  const host = normalizeClientHost(input)
  if (!host) return false
  if (host === 'localhost' || host === '127.0.0.1') return true
  return Boolean(root && host === root)
}

/** Public href for platform admin: custom domain, else /slug (subdomain is extra). */
export function shopStorefrontHref(shop: { slug?: string | null, customDomain?: string | null } | null | undefined): string {
  const custom = normalizeShopDomain(shop?.customDomain)
  if (custom) return `https://${custom}`
  const slug = String(shop?.slug || '').trim().toLowerCase()
  return slug ? `/${slug}` : '/'
}
